import { useEngineBus } from '../hooks/useEngineBus.js';

/**
 * @file The money readout — `$1,234` in the display font, docked under the
 * wanted stars in the top-right cluster. The genre's own layout: health and the
 * map bottom-left, heat and money top-right.
 *
 * ⚠ This component does NOT position itself, for the same reason `HealthBar`
 * does not: the top-right cluster in `routes/PlayRoute.jsx` owns the layout, and
 * two self-positioning slabs in one corner drift apart the moment either changes
 * size.
 *
 * It returns `null` until the first `'player:cash'` (a dev scene with no wallet
 * has no money), which is why `systems/Wallet.js` publishes on CONSTRUCTION as
 * well as on every change — without that emit the slot would stay blank until
 * the player's first purchase, and the first purchase is the one that needs the
 * number to have been there beforehand.
 *
 * The channel is low-rate by nature (a sale, a mission payout), so the throttle
 * here is generous: it exists to coalesce a payout landing in the same frame as
 * a purchase, not to tame a stream.
 *
 * @param {Object} props
 * @param {import('../../engine/core/Engine.js').Engine|null} props.engine
 */
export default function CashBar({ engine }) {
  const payload = useEngineBus(engine, 'player:cash', { throttle: 120 });
  if (!payload) return null;

  const cash = Math.max(0, Math.floor(Number(payload.cash) || 0));

  return (
    <div
      className="select-none rounded-md border border-hairline bg-void/70 px-2.5 py-1 backdrop-blur-sm"
      role="status"
      aria-label={`Cash ${cash} dollars`}
    >
      <div className="flex items-baseline justify-end gap-[2px]">
        {/* GOLD, not a fourth accent: the palette has exactly three (see
            `styles.css`) and money is the one thing gold has always meant in
            this genre. The sign is its own node and a step smaller — at 22 px
            the glyph is optically heavier than the digits and starts reading as
            part of the number's weight rather than as a unit marker. */}
        <span className="gta-outline font-display text-[15px] leading-none text-gold/80">$</span>
        <span className="gta-outline font-display text-[22px] leading-none text-gold tabular-nums">
          {cash.toLocaleString('en-US')}
        </span>
      </div>
    </div>
  );
}
